import React, { lazy, Suspense } from 'react';
import { routesPaths } from 'constants/routesPaths';
import HomePage from 'pages/HomePage';
import PreviewHomePage from 'pages/PreviewHomePage';
import CelebrityIdPage from 'pages/CelebrityIdPage';
import SeriesPage from 'pages/SeriesPage';
import NarrativesPage from 'pages/NarrativesPage';
import NotFoundPage from 'pages/NotFoundPage';
import CategoryPage from 'pages/CategoryPage';
import LoginPage from 'pages/LoginPage';
import VerifyPhonePage from 'pages/VerifyPhonePage';
import SignUpPage from 'pages/SignUpPage';
import LegalPage from 'pages/LegalPage';
import LivePlayerPage from 'pages/LivePlayerPage';
import AccountPage from 'pages/AccountPage';
import ScheduledLivePlayerPage from 'pages/ScheduledLivePlayerPage';
import BecomeCreator from 'pages/BecomeCreator';
import AddNarratives from 'pages/AddNarratives';
import AddOriginals from 'pages/AddOriginals';
import AdManager from 'pages/AdManagerPage';
import AddVideoAds from 'pages/AddVideoAds';
import AddBannerAdPage from 'pages/AddBannerAdPage';
import AddWidgetAdPage from 'pages/AddWidgetAdpage';
import ManageVideoAds from 'pages/Manage-Video-Ads';
import AdDetails from 'pages/AdDetails';
import Plans from 'components/AdManager/Plans';
import SearchPage from 'pages/SearchPage';
import AddEvent from 'pages/AddEventPage';
import AddPodcast from 'pages/AddPodcast';
import EventPage from 'pages/EventPage';
import PodcastPage from 'pages/PodcastPage';
import MockCreatorFinancePage from 'pages/MockCreatorFinancePage';
// MockIdentifiAdminPage and MockAdminLoginPage now loaded via lazy imports above
import MockOrganizationDashboardPage from 'pages/MockOrganizationDashboardPage';

const GoLive = lazy(() => import('pages/GoLive'));

const LazyCreatorDashboard = lazy(() => import('pages/creator/CreatorDashboardPage'));
const LazyCreatorSubPage = lazy(() => import('pages/creator/CreatorSubPage'));
const LazyAdminDashboard = lazy(() => import('pages/admin/AdminDashboardPage'));
const LazyAdminSubPage = lazy(() => import('pages/admin/AdminSubPage'));
const LazyAdminLoginPage = lazy(() => import('pages/admin/AdminLoginPage'));
const LazyFeedbackAdminPage = lazy(() => import('pages/admin/FeedbackAdminPage'));
const LazyOrgDashboard = lazy(() => import('pages/org/OrgDashboardPage'));
const LazyOrgSubPage = lazy(() => import('pages/org/OrgSubPage'));

const withSuspense = (Component, props = {}) => {
  const Wrapped = () => (
    <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0c0c0f' }} />}>
      <Component {...props} />
    </Suspense>
  );
  return Wrapped;
};

const IS_PREVIEW_BUILD = process.env.ENV === 'preview';

const routes = [
  {
    path: routesPaths.home,
    component: IS_PREVIEW_BUILD ? PreviewHomePage : HomePage,
    exact: true,
    layout: !IS_PREVIEW_BUILD,
    disableFooter: IS_PREVIEW_BUILD
  },
  {
    path: routesPaths.celebrityId,
    component: CelebrityIdPage
  },
  {
    path: routesPaths.series,
    component: SeriesPage,
    layout: false,
    exact: true
  },
  {
    path: routesPaths.narratives,
    component: NarrativesPage,
    layout: false,
    exact: true
  },
  {
    path: routesPaths.category,
    component: CategoryPage,
    customHeader: true,
    exact: true
  },
  {
    path: routesPaths.login,
    component: LoginPage,
    disableFooter: true,
    exact: true
  },
  {
    path: routesPaths.signup,
    component: SignUpPage,
    disableFooter: true,
    exact: true
  },
  {
    path: routesPaths.goLive,
    component: props => (
      <Suspense fallback={<div>Loading...</div>}>
        <GoLive {...props} />
      </Suspense>
    ),
    disableFooter: true,
    exact: true,
    layout: false
  },
  {
    path: routesPaths.verifyPhone,
    component: VerifyPhonePage,
    disableFooter: true,
    exact: true
  },
  {
    path: routesPaths.live,
    component: LivePlayerPage,
    layout: false,
    exact: true
  },
  {
    path: routesPaths.scheduledLive,
    component: ScheduledLivePlayerPage,
    layout: false,
    exact: true
  },
  {
    path: routesPaths.legal,
    component: LegalPage,
    exact: true
  },
  {
    path: routesPaths.account,
    component: AccountPage,
    private: true,
    exact: true
  },
  {
    path: routesPaths.goLive,
    component: GoLive,
    layout: false,
    exact: true
  },
  {
    path: routesPaths.becomeCreator,
    component: BecomeCreator,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.addNarrative,
    component: AddNarratives,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.editNarrative,
    component: AddNarratives,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.addOriginals,
    component: AddOriginals,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.editOriginals,
    component: AddOriginals,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.editEpisode,
    component: AddOriginals,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.adManager,
    component: AdManager,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.addvideoAds,
    component: AddVideoAds,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.individualaddvideoAds,
    component: AddVideoAds,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.individualaddbannerAds,
    component: AddBannerAdPage,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.editvideoAds,
    component: AddVideoAds,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.managevideoAdsNarrative,
    component: ManageVideoAds,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.managevideoAdsSeries,
    component: ManageVideoAds,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.addBannerAd,
    component: AddBannerAdPage,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.editBannerAd,
    component: AddBannerAdPage,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.addWidgetAd,
    component: AddWidgetAdPage,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.individualaddwidgetAds,
    component: AddWidgetAdPage,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.editWidgetAds,
    component: AddWidgetAdPage,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.adDetails,
    component: AdDetails,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.searchPage,
    component: SearchPage,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.addEvent,
    component: AddEvent,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.goLiveNow,
    component: AddEvent,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.editEvent,
    component: AddEvent,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.editEvent,
    component: AddEvent,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.event,
    component: EventPage,
    layout: false,
    exact: true
  },
  {
    path: routesPaths.addPodcast,
    component: AddPodcast,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.editPodcast,
    component: AddPodcast,
    private: true,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.podcast,
    component: PodcastPage,
    layout: false,
    exact: true
  },
  {
    path: routesPaths.plans,
    component: Plans,
    private: true,
    exact: true,
    disableFooter: true
  },
  // ── Creator dashboard (role-gated: Celebrity) ──
  {
    path: routesPaths.creatorDashboard,
    component: withSuspense(LazyCreatorDashboard),
    allowedRoles: ['Celebrity'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.creatorFinance,
    component: withSuspense(LazyCreatorSubPage, {
      sectionKey: 'creatorFinance',
      title: 'Revenue & payouts',
      description: 'Earnings breakdown, payout history, and Stripe Connect management.'
    }),
    allowedRoles: ['Celebrity'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.creatorContent,
    component: withSuspense(LazyCreatorSubPage, {
      sectionKey: 'creatorContent',
      title: 'Content',
      description: 'Manage narratives, originals, episodes, and podcasts.'
    }),
    allowedRoles: ['Celebrity'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.creatorAnalytics,
    component: withSuspense(LazyCreatorSubPage, {
      sectionKey: 'creatorAnalytics',
      title: 'Fans & CRM',
      description: 'Audience intelligence, subscriber analytics, and engagement campaigns.'
    }),
    allowedRoles: ['Celebrity'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.creatorEvents,
    component: withSuspense(LazyCreatorSubPage, {
      sectionKey: 'creatorEvents',
      title: 'Live event center',
      description: 'Schedule, manage, and monetize live streaming events.'
    }),
    allowedRoles: ['Celebrity'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.creatorAds,
    component: withSuspense(LazyCreatorSubPage, {
      sectionKey: 'creatorAds',
      title: 'Ads',
      description: 'Ad campaigns, sponsorships, and monetization tools.'
    }),
    allowedRoles: ['Celebrity'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.creatorSettings,
    component: withSuspense(LazyCreatorSubPage, {
      sectionKey: 'creatorSettings',
      title: 'Settings',
      description: 'Creator profile, payout preferences, and account configuration.'
    }),
    allowedRoles: ['Celebrity'],
    layout: false,
    exact: true
  },

  // ── Admin dashboard (role-gated: Admin) ──
  {
    path: routesPaths.adminLogin,
    component: withSuspense(LazyAdminLoginPage),
    layout: false,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.adminDashboard,
    component: withSuspense(LazyAdminDashboard),
    allowedRoles: ['Admin'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.adminUsers,
    component: withSuspense(LazyAdminSubPage, {
      sectionKey: 'adminUsers',
      title: 'Users & creators',
      description: 'Search, review, and manage platform users, creators, and organizations.'
    }),
    allowedRoles: ['Admin'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.adminContent,
    component: withSuspense(LazyAdminSubPage, {
      sectionKey: 'adminContent',
      title: 'Content moderation',
      description: 'Review flagged content, takedown requests, and publishing policies.'
    }),
    allowedRoles: ['Admin'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.adminAds,
    component: withSuspense(LazyAdminSubPage, {
      sectionKey: 'adminAds',
      title: 'Platform ad operations',
      description:
        'House ads, cross-creator network buys, discovery boosts, and tentpole sponsorships across every surface on Identifi.'
    }),
    allowedRoles: ['Admin'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.adminFinance,
    component: withSuspense(LazyAdminSubPage, {
      sectionKey: 'adminFinance',
      title: 'Platform finance',
      description: 'Platform revenue, creator payouts, disputes, and reconciliation.'
    }),
    allowedRoles: ['Admin'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.adminSettings,
    component: withSuspense(LazyAdminSubPage, {
      sectionKey: 'adminSettings',
      title: 'Platform settings',
      description: 'Fee configuration, webhook health, and operational controls.'
    }),
    allowedRoles: ['Admin'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.adminFeedback,
    component: withSuspense(LazyFeedbackAdminPage),
    layout: false,
    exact: true,
    disableFooter: true
  },

  // ── Organization dashboard (role-gated: Organization) ──
  {
    path: routesPaths.orgDashboard,
    component: withSuspense(LazyOrgDashboard),
    allowedRoles: ['Organization'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.orgTeam,
    component: withSuspense(LazyOrgSubPage, {
      sectionKey: 'orgTeam',
      title: 'Teams & permissions',
      description: 'Manage creators, club operators, and media managers.'
    }),
    allowedRoles: ['Organization'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.orgEvents,
    component: withSuspense(LazyOrgSubPage, {
      sectionKey: 'orgEvents',
      title: 'Event scheduling',
      description: 'Season calendar, broadcast readiness, and venue management.'
    }),
    allowedRoles: ['Organization'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.orgFinance,
    component: withSuspense(LazyOrgSubPage, {
      sectionKey: 'orgFinance',
      title: 'Revenue & settlements',
      description: 'Revenue sharing, team settlements, and sponsor receivables.'
    }),
    allowedRoles: ['Organization'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.orgAds,
    component: withSuspense(LazyOrgSubPage, {
      sectionKey: 'orgAds',
      title: 'Sponsorship & ads',
      description:
        'Season sponsors, in-venue signage, broadcast inventory, and matchday digital placements.'
    }),
    allowedRoles: ['Organization'],
    layout: false,
    exact: true
  },
  {
    path: routesPaths.orgSettings,
    component: withSuspense(LazyOrgSubPage, {
      sectionKey: 'orgSettings',
      title: 'Settings & branding',
      description: 'Branding, permissions, custom domains, and white-label configuration.'
    }),
    allowedRoles: ['Organization'],
    layout: false,
    exact: true
  },

  // ── Legacy mock routes (backward compatibility) ──
  {
    path: routesPaths.mockCreatorFinance,
    component: MockCreatorFinancePage,
    layout: false,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.mockOrganizationDashboard,
    component: MockOrganizationDashboardPage,
    layout: false,
    exact: true,
    disableFooter: true
  },
  {
    path: routesPaths.home,
    component: NotFoundPage,
    disableFooter: true
  }
];

export default routes;
